import { 
  collection, 
  addDoc, 
  doc, 
  updateDoc, 
  getDoc, 
  getDocs, 
  query, 
  where, 
  orderBy, 
  serverTimestamp,
  writeBatch
} from 'firebase/firestore';
import { db, auth } from './firebase';
import { 
  createThread, 
  sendMessageAndWaitForResponse, 
  generateChatTitle,
  sendMessageWithRealStreaming,
  sendMessageWithFilesAndStream
} from './openaiService';

// Helper function to check authentication
const checkAuth = () => {
  if (!auth.currentUser) {
    throw new Error('User must be authenticated');
  }
  return auth.currentUser;
};

// Create a new chat
export async function createChat(userId, title = 'New Chat') {
  try {
    // Check authentication
    checkAuth();
    
    // Make sure userId matches current user
    if (userId !== auth.currentUser.uid) {
      throw new Error('User ID mismatch');
    }
    
    // Create a new OpenAI thread
    const threadId = await createThread();
    
    // Create chat document in Firestore
    const chatRef = await addDoc(collection(db, 'chats'), {
      userId,
      threadId,
      title,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      messages: []
    });
    
    return {
      id: chatRef.id,
      threadId,
      title,
      createdAt: new Date(),
      updatedAt: new Date(),
      messages: []
    };
  } catch (error) {
    console.error('Error creating chat:', error);
    throw error;
  }
}

// Get all chats for a user
export async function getUserChats(userId) {
  try {
    // Check authentication
    checkAuth();
    
    // Make sure userId matches current user
    if (userId !== auth.currentUser.uid) {
      throw new Error('User ID mismatch');
    }
    
    const q = query(
      collection(db, 'chats'),
      where('userId', '==', userId),
      orderBy('updatedAt', 'desc')
    );
    
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      // Convert Firestore timestamps to JS Dates
      createdAt: doc.data().createdAt?.toDate(),
      updatedAt: doc.data().updatedAt?.toDate()
    }));
  } catch (error) {
    console.error('Error getting user chats:', error);
    throw error;
  }
}

// Get a specific chat
export async function getChat(chatId) {
  try {
    // Check authentication
    checkAuth();
    
    const chatDoc = await getDoc(doc(db, 'chats', chatId));
    
    if (!chatDoc.exists()) {
      throw new Error('Chat not found');
    }
    
    const chatData = chatDoc.data();
    
    // Verify the chat belongs to current user
    if (chatData.userId !== auth.currentUser.uid) {
      throw new Error('Unauthorized access to chat');
    }
    
    return {
      id: chatDoc.id,
      ...chatData,
      // Convert Firestore timestamps to JS Dates
      createdAt: chatData.createdAt?.toDate(),
      updatedAt: chatData.updatedAt?.toDate()
    };
  } catch (error) {
    console.error('Error getting chat:', error);
    throw error;
  }
}

// Update chat title
export async function updateChatTitle(chatId, title) {
  try {
    // Check authentication
    checkAuth();
    
    // Verify the chat exists and belongs to current user
    const chatDoc = await getDoc(doc(db, 'chats', chatId));
    if (!chatDoc.exists()) {
      throw new Error('Chat not found');
    }
    
    const chatData = chatDoc.data();
    if (chatData.userId !== auth.currentUser.uid) {
      throw new Error('Unauthorized access to chat');
    }
    
    await updateDoc(doc(db, 'chats', chatId), {
      title,
      updatedAt: serverTimestamp()
    });
    
    return title; // Return the new title
  } catch (error) {
    console.error('Error updating chat title:', error);
    throw error;
  }
}

// Add a message to a chat
export async function addMessageToChat(chatId, message, companyInfo = null) {
  try {
    // Check authentication
    checkAuth();
    
    // Get the chat
    const chat = await getChat(chatId);
    
    // Add user message to messages array
    const userMessage = {
      role: 'user',
      content: message,
      timestamp: new Date()
    };
    
    const messages = [...(chat.messages || []), userMessage];
    
    // Update chat document with user message
    await updateDoc(doc(db, 'chats', chatId), {
      messages,
      updatedAt: serverTimestamp()
    });
    
    // Send message to OpenAI and get response
    const response = await sendMessageAndWaitForResponse(chat.threadId, message, companyInfo);
    
    // Process response content
    const responseContent = response.content.map(item => ({
      type: item.type,
      text: item.type === 'text' ? item.text.value : null,
      // Add support for code, links, etc. based on the item type
    }));
    
    // Add assistant message to messages array
    const assistantMessage = {
      role: 'assistant',
      content: responseContent,
      timestamp: new Date()
    };
    
    const updatedMessages = [...messages, assistantMessage];
    
    // Update chat document with assistant message
    await updateDoc(doc(db, 'chats', chatId), {
      messages: updatedMessages,
      updatedAt: serverTimestamp()
    });
    
    return {
      userMessage,
      assistantMessage
    };
  } catch (error) {
    console.error('Error adding message to chat:', error);
    throw error;
  }
}

// Delete a chat
export async function deleteChat(chatId) {
  try {
    // Check authentication
    checkAuth();
    
    // Verify the chat exists and belongs to current user
    const chatDoc = await getDoc(doc(db, 'chats', chatId));
    if (!chatDoc.exists()) {
      throw new Error('Chat not found');
    }
    
    const chatData = chatDoc.data();
    if (chatData.userId !== auth.currentUser.uid) {
      throw new Error('Unauthorized access to chat');
    }
    
    // Get all messages for this chat
    const messagesRef = collection(db, 'messages');
    const q = query(messagesRef, where('chatId', '==', chatId));
    const messagesSnapshot = await getDocs(q);
    
    // Create a batch for efficient deletion
    const batch = writeBatch(db);
    
    // Add all message documents to the batch for deletion
    messagesSnapshot.forEach((messageDoc) => {
      batch.delete(messageDoc.ref);
    });
    
    // Add the chat document to the batch for deletion
    batch.delete(doc(db, 'chats', chatId));
    
    // Commit the batch
    await batch.commit();
    
    console.log(`Successfully deleted chat ${chatId} and all associated messages`);
    return true;
  } catch (error) {
    console.error('Error deleting chat:', error);
    throw error;
  }
}

// Add a streaming message to chat
export async function addMessageToChatStreaming(chatId, message, companyInfo = null, onStreamUpdate, isFirstMessage = false) {
  try {
    // Check authentication
    checkAuth();
    
    console.log('Starting streaming message for chat:', chatId);
    
    // Get the chat
    const chatDoc = await getDoc(doc(db, 'chats', chatId));
    
    if (!chatDoc.exists()) {
      throw new Error('Chat not found');
    }
    
    const chatData = chatDoc.data();
    
    // Verify the chat belongs to current user
    if (chatData.userId !== auth.currentUser.uid) {
      throw new Error('Unauthorized access to chat');
    }
    
    const chat = {
      id: chatDoc.id,
      ...chatData,
      // Convert Firestore timestamps to JS Dates
      createdAt: chatData.createdAt?.toDate(),
      updatedAt: chatData.updatedAt?.toDate()
    };
    
    // Check if this is the first message
    const isFirstMsg = isFirstMessage || (!chat.messages || chat.messages.length === 0);
    console.log('Is first message:', isFirstMsg);
    
    // Add user message to messages array
    const userMessage = {
      role: 'user',
      content: message, // This now contains the extracted text from files
      timestamp: new Date()
    };
    
    const messages = [...(chat.messages || []), userMessage];
    
    // Update chat document with user message
    await updateDoc(doc(db, 'chats', chatId), {
      messages,
      updatedAt: serverTimestamp()
    });
    
    console.log('User message added to Firestore');
    
    // Create initial assistant message
    const assistantMessage = {
      role: 'assistant',
      content: [{
        type: 'text',
        text: { value: 'Thinking...' }
      }],
      timestamp: new Date()
    };
    
    // Call onUpdate with initial message
    onStreamUpdate(assistantMessage);
    
    try {
      // Define the stream handler to handle updates
      const handleStreamUpdate = (updatedAssistantMessage, isFinal) => {
        console.log('Stream update received, isFinal:', isFinal);
        
        // Process the content format for storing in Firestore
        let processedContent = [];
        
        if (updatedAssistantMessage.content) {
          processedContent = updatedAssistantMessage.content.map(item => {
            if (item.type === 'text') {
              return {
                type: 'text',
                text: {
                  value: typeof item.text === 'object' ? item.text.value : item.text
                }
              };
            }
            return item;
          });
        }
        
        // Create a properly formatted message
        const formattedMessage = {
          role: 'assistant',
          content: processedContent,
          timestamp: updatedAssistantMessage.timestamp || new Date(),
          isStreaming: !isFinal
        };
        
        // Send the formatted message to the UI
        onStreamUpdate(formattedMessage, isFinal);
        
        // If this is the final message, update Firestore
        if (isFinal && processedContent.length > 0) {
          console.log('Updating Firestore with final message');
          // Update Firestore with the final message
          const updatedMessages = [...messages, formattedMessage];
          updateDoc(doc(db, 'chats', chatId), {
            messages: updatedMessages,
            updatedAt: serverTimestamp()
          }).catch(err => {
            console.error('Error updating Firestore with final message:', err);
          });
        }
      };
      
      // Use real-time streaming - here the message already contains the extracted text
      console.log('Starting real-time streaming with message containing extracted text');
      const messageId = await sendMessageWithRealStreaming(
        chat.threadId, 
        message, 
        companyInfo,
        handleStreamUpdate
      );
      
      console.log('Streaming completed, messageId:', messageId);
      
      // Store the result data to return
      const result = {
        userMessage,
        assistantMessage: null,
        newTitle: null // Initialize with null, will be set if a title is generated
      };
      
      // If this is the first message, generate an AI title
      if (isFirstMsg && message.trim() !== '') {
        console.log('Generating title for first message');
        try {
          // Generate an AI-powered title for the chat
          const aiTitle = await generateChatTitle(chat.threadId, message);
          
          // Update the chat title
          if (aiTitle && aiTitle.trim() !== '') {
            await updateChatTitle(chatId, aiTitle);
            
            // Store the new title in the result
            result.newTitle = aiTitle;
            
            // Dispatch a custom event that components can listen for
            if (typeof window !== 'undefined') {
              const titleUpdateEvent = new CustomEvent('chat-title-updated', { 
                detail: {
                  chatId,
                  newTitle: aiTitle
                }
              });
              window.dispatchEvent(titleUpdateEvent);
              console.log('Title update event dispatched:', aiTitle);
              
              // Store chat ID in localStorage to ensure it's picked up by the sidebar
              localStorage.setItem('justCreatedChatId', chatId);
            }
          }
        } catch (titleError) {
          console.error('Error generating AI chat title:', titleError);
          
          // Fallback to simple title extraction if AI generation fails
          try {
            // Extract a short title from the message
            let newTitle = '';
            
            if (message.length <= 30) {
              newTitle = message; // Use the message as title if it's short
            } else {
              // Extract the first sentence or first 30 chars
              newTitle = message.split(/[.!?]/)[0];
              if (newTitle.length > 30) {
                newTitle = message.substring(0, 30) + '...';
              }
            }
            
            // Update the chat title
            if (newTitle.trim() !== '') {
              await updateChatTitle(chatId, newTitle);
              
              // Store the new title in the result
              result.newTitle = newTitle;
              
              // Dispatch a custom event that components can listen for
              if (typeof window !== 'undefined') {
                const titleUpdateEvent = new CustomEvent('chat-title-updated', { 
                  detail: {
                    chatId,
                    newTitle
                  }
                });
                window.dispatchEvent(titleUpdateEvent);
                console.log('Fallback title update event dispatched:', newTitle);
                
                // Store chat ID in localStorage to ensure it's picked up by the sidebar
                localStorage.setItem('justCreatedChatId', chatId);
              }
            }
          } catch (fallbackError) {
            console.error('Error with fallback title generation:', fallbackError);
            // Continue without generating a title
          }
        }
      }
      
      return result;
    } catch (error) {
      // If there's an error in the OpenAI call, update with error message
      console.error('OpenAI error:', error);
      const errorMessage = {
        role: 'assistant',
        content: [{
          type: 'text',
          text: { value: `Error: ${error.message || 'An error occurred while processing your request.'}` }
        }],
        timestamp: new Date()
      };
      
      // Update UI with error
      onStreamUpdate(errorMessage, true);
      
      // Update chat document with error message
      const updatedMessages = [...messages, errorMessage];
      await updateDoc(doc(db, 'chats', chatId), {
        messages: updatedMessages,
        updatedAt: serverTimestamp()
      });
      
      throw error;
    }
  } catch (error) {
    console.error('Error adding streaming message to chat:', error);
    throw error;
  }
}

// Add a streaming message with files to chat
export async function addMessageToChatStreamingWithFiles(chatId, message, files, companyInfo = null, onStreamUpdate, isFirstMessage = false) {
  try {
    // Check authentication
    checkAuth();
    
    console.log('Starting streaming message with files for chat:', chatId);
    
    // Get the chat
    const chatDoc = await getDoc(doc(db, 'chats', chatId));
    
    if (!chatDoc.exists()) {
      throw new Error('Chat not found');
    }
    
    const chatData = chatDoc.data();
    
    // Verify the chat belongs to current user
    if (chatData.userId !== auth.currentUser.uid) {
      throw new Error('Unauthorized access to chat');
    }
    
    const chat = {
      id: chatDoc.id,
      ...chatData,
      // Convert Firestore timestamps to JS Dates
      createdAt: chatData.createdAt?.toDate(),
      updatedAt: chatData.updatedAt?.toDate()
    };
    
    // Check if this is the first message
    const isFirstMsg = isFirstMessage || (!chat.messages || chat.messages.length === 0);
    console.log('Is first message:', isFirstMsg);
    
    // Create file references for storing in Firestore
    const fileReferences = files.map(file => ({
      name: file.name,
      type: file.type,
      size: file.size,
      uploadedAt: new Date()
    }));
    
    // Add user message to messages array
    const userMessage = {
      role: 'user',
      content: message,
      timestamp: new Date(),
      files: fileReferences // Add file information
    };
    
    const messages = [...(chat.messages || []), userMessage];
    
    // Update chat document with user message
    await updateDoc(doc(db, 'chats', chatId), {
      messages,
      updatedAt: serverTimestamp()
    });
    
    console.log('User message with files added to Firestore');
    
    // Create initial assistant message
    const assistantMessage = {
      role: 'assistant',
      content: [{
        type: 'text',
        text: { value: 'Thinking...' }
      }],
      timestamp: new Date()
    };
    
    // Call onUpdate with initial message
    onStreamUpdate(assistantMessage);
    
    try {
      // Define the stream handler to handle updates
      const handleStreamUpdate = (updatedAssistantMessage, isFinal) => {
        console.log('Stream update received, isFinal:', isFinal);
        
        // Process the content format for storing in Firestore
        let processedContent = [];
        let fileIds = [];
        
        if (updatedAssistantMessage.content) {
          processedContent = updatedAssistantMessage.content.map(item => {
            if (item.type === 'text') {
              // Check for file annotations
              if (item.text && item.text.annotations) {
                item.text.annotations.forEach(ann => {
                  if (ann.type === 'file_path' && ann.file_path && ann.file_path.file_id) {
                    fileIds.push(ann.file_path.file_id);
                  }
                });
              }
              return {
                type: 'text',
                text: {
                  value: typeof item.text === 'object' ? item.text.value : item.text
                }
              };
            } else if (item.type === 'image_file' && item.image_file) {
              // Capture image file IDs
              if (item.image_file.file_id) {
                fileIds.push(item.image_file.file_id);
              }
              return item;
            } else if (item.type === 'file_attachment' && item.file_attachment) {
              // Capture file attachment IDs
              if (item.file_attachment.file_id) {
                fileIds.push(item.file_attachment.file_id);
              }
              return item;
            }
            return item;
          });
        }
        
        // Check for fileId and fileIds in the message object
        if (updatedAssistantMessage.fileId && !fileIds.includes(updatedAssistantMessage.fileId)) {
          fileIds.push(updatedAssistantMessage.fileId);
        }
        
        if (updatedAssistantMessage.fileIds && Array.isArray(updatedAssistantMessage.fileIds)) {
          updatedAssistantMessage.fileIds.forEach(id => {
            if (!fileIds.includes(id)) fileIds.push(id);
          });
        }
        
        // Create a properly formatted message with file IDs
        const formattedMessage = {
          role: 'assistant',
          content: processedContent,
          timestamp: updatedAssistantMessage.timestamp || new Date(),
          isStreaming: !isFinal,
          fileIds: fileIds.length > 0 ? fileIds : undefined
        };
        
        // Send the formatted message to the UI
        onStreamUpdate(formattedMessage, isFinal);
        
        // If this is the final message, update Firestore
        if (isFinal && processedContent.length > 0) {
          console.log('Updating Firestore with final message');
          // Update Firestore with the final message
          const updatedMessages = [...messages, formattedMessage];
          updateDoc(doc(db, 'chats', chatId), {
            messages: updatedMessages,
            updatedAt: serverTimestamp()
          }).catch(err => {
            console.error('Error updating Firestore with final message:', err);
          });
        }
      };
      
      // Use real-time streaming with files
      console.log('Starting real-time streaming with files');
      const messageId = await sendMessageWithFilesAndStream(
        chat.threadId, 
        message, 
        files,
        companyInfo,
        handleStreamUpdate
      );
      
      console.log('Streaming with files completed, messageId:', messageId);
      
      // Store the result data to return
      const result = {
        userMessage,
        assistantMessage: null,
        newTitle: null // Initialize with null, will be set if a title is generated
      };
      
      // Handle title generation (same as for regular messages)
      if (isFirstMsg && message.trim() !== '') {
        // Existing title generation code here
        // ... (keep the existing code for title generation)
      }
      
      return result;
    } catch (error) {
      // Handle error (same as for regular messages)
      // ... (keep the existing error handling code)
    }
  } catch (error) {
    console.error('Error adding streaming message with files to chat:', error);
    throw error;
  }
}

// Update a message and truncate subsequent messages
export async function updateMessageAndTruncate(chatId, messageIndex, newContent) {
  try {
    // Check authentication
    checkAuth();
    
    // Get the chat
    const chatDoc = await getDoc(doc(db, 'chats', chatId));
    
    if (!chatDoc.exists()) {
      throw new Error('Chat not found');
    }
    
    const chatData = chatDoc.data();
    
    // Verify the chat belongs to current user
    if (chatData.userId !== auth.currentUser.uid) {
      throw new Error('Unauthorized access to chat');
    }
    
    // Get the messages
    const messages = chatData.messages || [];
    
    // Check if the message index is valid
    if (messageIndex < 0 || messageIndex >= messages.length) {
      throw new Error('Invalid message index');
    }
    
    // Get the message to update
    const messageToUpdate = messages[messageIndex];
    
    // Only user can edit their own messages
    if (messageToUpdate.role !== 'user') {
      throw new Error('Can only edit user messages');
    }
    
    // Get all messages up to the edited message
    const messagesUpToEdit = messages.slice(0, messageIndex + 1);
    
    // Update the content of the message being edited
    messagesUpToEdit[messageIndex] = {
      ...messageToUpdate,
      content: newContent,
      edited: true,
      editedAt: new Date()
    };
    
    // Update chat document with truncated messages
    await updateDoc(doc(db, 'chats', chatId), {
      messages: messagesUpToEdit,
      updatedAt: serverTimestamp()
    });
    
    return messagesUpToEdit;
  } catch (error) {
    console.error('Error updating message and truncating:', error);
    throw error;
  }
}